import type { z } from "zod";
import { localProductSchema } from "../schemas/localProductSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@chakra-ui/react";
import { useEffect } from "react";
import type { LocalProductInput } from "../types/LocalProduct";
import { useCreateLocalProduct } from "../useCases/useCreateLocalProduct";
import LocalProductForm from "./LocalProductForm";
import { useAuth } from "@/shared/session";
import { productApi } from "@/shared/core-products";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { toast } from "@/ui/chakra/toaster";

type LocalProductFormInput = z.infer<typeof localProductSchema>;

interface CreateLocalProductModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateLocalProductModal = ({
  isOpen,
  onClose,
}: CreateLocalProductModalProps) => {
  const { mutate, isPending } = useCreateLocalProduct();
  const { session } = useAuth();

  const { handleSubmit, control, reset } = useForm<LocalProductFormInput>({
    resolver: zodResolver(localProductSchema),
    defaultValues: {
      code: "",
      comment: "",
      purchasePrice: 0,
      maximumStock: 0,
      description: "",
      unit: "",
      location: "",
      stock: 0,
      minimumStock: 0,
      converter: 0,
      converterTypeId: "",
      storageId: "",
      zoneId: session?.zone ?? "",
      images: [],
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleCreateLocalProduct = (data: LocalProductInput) => {
    if (!session?.zone) return;

    if (data.images && data.images.length > 5) {
      toast({
        title: "Error al crear producto local.",
        description: "No puedes subir más de 5 imágenes.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      ...data,
      zoneId: session.zone,
    };
    const imageFiles = (payload.images ?? []).filter(
      (img): img is File => img instanceof File,
    );
    mutate(payload, {
      onSuccess: async (data) => {
        if (data.id && imageFiles.length > 0) {
          await productApi.uploadImageLocalProduct(data.id, imageFiles[0]);
        }
        onClose();
        toast({
          title: "Producto creado.",
          description: "El producto local se ha creado exitosamente.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: "Error al crear producto local.",
          description:
            error.message || "Ocurrió un error al crear el producto local.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      },
    });
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <DialogContent>
        <form onSubmit={handleSubmit(handleCreateLocalProduct)}>
          <DialogHeader>
            <DialogTitle>Crear producto local</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <LocalProductForm control={control} />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isPending}>
              Crear
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
